import { zodResolver } from '@hookform/resolvers/zod';
import {
	Accordion,
	Button,
	Checkbox,
	Select,
	Textarea,
	TextInput,
	UnstyledButton
} from '@mantine/core';
import { PhoneController } from '@newo/ui';
import { ArrowRight } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import PhoneInput from '@/components/PhoneInput';
import { useHostContext } from '@/context/HostContext.tsx';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app.ts';
import { AgentTrainingStep } from '@/types/agent-training.ts';
import type { CustomerData } from '@/types/common.ts';
import { cn } from '@/utils';
import { createCustomerSchema } from '@/utils/validation.ts';
import styles from './ContactForm.module.css';

interface AgentSaveFormProps {
	agentName?: string;
	onSubmit: (data: CustomerData) => void;
	error?: string;
}

const ContactForm: FC<AgentSaveFormProps> = ({ error, onSubmit }) => {
	const [isTermsChecked, setTerms] = useState(false);
	const { setTrainingStep, partner_idn, member_idn } = useAgentTraining();
	const { isMiniCreatorMode, isPageMode, localization } = useHostContext();
	const customerData = useAppStore((state) => state.customerData);

	const formSchema = useMemo(() => {
		return createCustomerSchema({
			isExternalAccountNumberEnabled:
				partnerMetadata.isExternalAccountNumberEnabled(),
			translate: localization.translate
		});
	}, [localization.translate]);

	const {
		register,
		handleSubmit,
		getValues,
		formState: { errors },
		control
	} = useForm<CustomerData>({
		resolver: zodResolver(formSchema),
		mode: 'all',
		defaultValues: {
			contact_fullname: customerData?.contact_fullname,
			contact_email: customerData?.contact_email,
			contact_phone: customerData?.contact_phone ?? '',
			creator_partner_idn: customerData?.account_managers,
			account_managers: customerData?.account_managers,
			country_code: customerData?.country_code?.toUpperCase() ?? '',
			promo_code: customerData?.promo_code ?? '',
			industry: customerData?.industry ?? '',
			comment: customerData?.comment ?? '',
			external_account_number: customerData?.external_account_number ?? '',
			partner_idn: partner_idn ?? '',
			partner_member_idn: member_idn ?? ''
		}
	});

	const { isValidating } = useFormState({ control });

	const hasErrors = useMemo(() => {
		const {
			contact_email: emailValue,
			contact_fullname: fullName,
			contact_phone: phoneValue
		} = getValues();

		const hasAllValues =
			Boolean(emailValue?.trim()) &&
			Boolean(fullName?.trim()) &&
			Boolean(phoneValue?.trim());

		return isValidating || !hasAllValues || Object.keys(errors).length > 0;
	}, [errors, isValidating, getValues]);

	const onFormSubmit = (data: CustomerData) => {
		onSubmit(data);
	};

	const handleBack = () => {
		setTrainingStep(AgentTrainingStep.Onboarding);
	};

	const labelClassName = cn('!text-sm ![font-family:var(--font-body)]', {
		'text-xs': isMiniCreatorMode
	});

	const aiCountryCodeValues = useMemo(() => {
		return [
			{
				label: localization.translate(
					'creator',
					'customerIdentification.partners_settings.ai_country_code.values.au'
				),
				value: 'AU'
			},
			{
				label: localization.translate(
					'creator',
					'customerIdentification.partners_settings.ai_country_code.values.ca'
				),
				value: 'CA'
			},
			{
				label: localization.translate(
					'creator',
					'customerIdentification.partners_settings.ai_country_code.values.nl'
				),
				value: 'NL'
			},
			{
				label: localization.translate(
					'creator',
					'customerIdentification.partners_settings.ai_country_code.values.gb'
				),
				value: 'GB'
			},
			{
				label: localization.translate(
					'creator',
					'customerIdentification.partners_settings.ai_country_code.values.us'
				),
				value: 'US'
			}
		];
	}, [localization]);
	return (
		<form
			className={cn(
				'flex flex-col justify-between w-full h-full min-h-0 !pr-[3px]',
				{
					'gap-3': isMiniCreatorMode
				}
			)}
			noValidate
			onSubmit={handleSubmit(onFormSubmit)}
		>
			<div
				className={cn('flex-1 min-h-0', {
					'sm:max-h-[410px]': !isMiniCreatorMode,
					[styles.container]: !isPageMode
				})}
			>
				{error ? <div className="text-red-500">{error}</div> : undefined}

				<div
					className={cn('flex flex-col gap-5 w-full pl-[3px] pr-[3px]', {
						'gap-4': isMiniCreatorMode
					})}
				>
					<Controller
						control={control}
						name="contact_fullname"
						render={({ field, fieldState }) => (
							<TextInput
								{...field}
								classNames={{ label: labelClassName }}
								description={
									localization.translate(
										'creator',
										'customerIdentification.customer_full_name.description'
									) || undefined
								}
								error={fieldState.error?.message}
								label={localization.translate(
									'creator',
									'customerIdentification.customer_full_name.label'
								)}
								placeholder={localization.translate(
									'creator',
									'customerIdentification.customer_full_name.placeholder'
								)}
								type="text"
								withAsterisk
							/>
						)}
					/>

					<Controller
						control={control}
						name="contact_email"
						render={({ field, fieldState }) => (
							<TextInput
								{...field}
								classNames={{ label: labelClassName }}
								description={localization.translate(
									'creator',
									'customerIdentification.customer_emails.description'
								)}
								error={fieldState.error?.message}
								label={localization.translate(
									'creator',
									'customerIdentification.customer_emails.label'
								)}
								placeholder={localization.translate(
									'creator',
									'customerIdentification.customer_emails.placeholder'
								)}
								type="email"
								withAsterisk
							/>
						)}
					/>

					<PhoneController
						control={control}
						name="contact_phone"
						render={(props) => (
							<PhoneInput
								{...props}
								className={cn({
									[styles.dropdownMini]: !isPageMode,
									[styles.dropdownPage]: isPageMode
								})}
								description={localization.translate(
									'creator',
									'customerIdentification.customer_phone_description'
								)}
							/>
						)}
						revalidateOn={localization.language}
					/>

					{partnerMetadata.isExternalAccountNumberEnabled() && (
						<TextInput
							classNames={{ label: labelClassName }}
							error={errors.external_account_number?.message}
							label={localization.translate(
								'creator',
								'customerIdentification.external_account_number.label'
							)}
							placeholder={localization.translate(
								'creator',
								'customerIdentification.external_account_number.placeholder'
							)}
							type="text"
							withAsterisk
							{...register('external_account_number')}
						/>
					)}

					<Checkbox
						checked={isTermsChecked}
						label={localization.rich(
							'creator',
							'customerIdentification.termsAgreement',
							{
								link: (chunk) => (
									<a
										className="link pl-[5px] text-black"
										href={partnerMetadata.getTermsOfServicesUrl()}
										rel="noreferrer"
										target="_blank"
									>
										{chunk}
									</a>
								)
							}
						)}
						onChange={(event) => setTerms(event.currentTarget.checked)}
					/>

					<div className="relative">
						<Accordion
							className="bg-[#FAFAFA]! rounded-[8px]! pt-[5px] pb-[5px]"
							classNames={{
								label: 'p-0!',
								control: 'border-none!',
								item: 'border-none!',
								panel: cn({
									'absolute left-0 right-0 z-40 bg-[#FAFAFA] rounded-[8px] shadow-md':
										isPageMode
								})
							}}
							multiple
						>
							<Accordion.Item value="For Partners">
								<Accordion.Control>
									{localization.translate(
										'creator',
										'customerIdentification.partners_settings.label'
									)}
								</Accordion.Control>
								<Accordion.Panel
									className={cn({
										'overflow-y-auto pr-[5px] max-h-60': isPageMode
									})}
								>
									<div className="flex flex-col gap-5 z-30">
										<Controller
											control={control}
											name="country_code"
											render={({ field: { onChange, value } }) => (
												<Select
													classNames={{ label: labelClassName }}
													data={aiCountryCodeValues}
													label={localization.translate(
														'creator',
														'customerIdentification.partners_settings.ai_country_code.label'
													)}
													onChange={onChange}
													value={value}
												/>
											)}
										/>

										<TextInput
											classNames={{ label: labelClassName }}
											error={errors.partner_idn?.message}
											label={localization.translate(
												'creator',
												'customerIdentification.partners_settings.partner_idn'
											)}
											{...register('partner_idn')}
										/>

										<TextInput
											classNames={{ label: labelClassName }}
											error={errors.partner_member_idn?.message}
											label={localization.translate(
												'creator',
												'customerIdentification.partners_settings.partner_member_idn'
											)}
											{...register('partner_member_idn')}
										/>

										<TextInput
											classNames={{ label: labelClassName }}
											error={errors.promo_code?.message}
											label={localization.translate(
												'creator',
												'customerIdentification.partners_settings.promo_code'
											)}
											{...register('promo_code')}
										/>

										<Textarea
											classNames={{ label: labelClassName }}
											error={errors.comment?.message}
											label={localization.translate(
												'creator',
												'customerIdentification.partners_settings.partner_comments'
											)}
											rows={5}
											{...register('comment')}
										/>
									</div>
								</Accordion.Panel>
							</Accordion.Item>
						</Accordion>
					</div>
				</div>
			</div>
			<div
				className={cn('flex w-full justify-between gap-3 shrink-0 pt-3', {
					'sticky bottom-0 bg-white z-10': isMiniCreatorMode
				})}
			>
				<UnstyledButton onClick={handleBack}>
					{localization.translate('common', 'back')}
				</UnstyledButton>

				<Button
					className={cn(
						'group !flex !w-full sm:!w-[221px] !h-[40px] !rounded-[12px]',
						{ '!opacity-50': !isTermsChecked || hasErrors }
					)}
					disabled={!isTermsChecked || hasErrors}
					rightSection={<ArrowRight size={20} />}
					type="submit"
				>
					{localization.translate('creator', 'customerIdentification.cta')}
				</Button>
			</div>
		</form>
	);
};

export default ContactForm;
